/**
 * Admin Command credits helpers (pure functions — unit-tested in
 * adminCredits.test.ts).
 *
 * Credits are TEAM-wide: one `get_credit_balance` call per team is enough,
 * made with any member's email (the n8n-proxy resolves that email's team
 * server-side and scopes the balance to it). Calling per user would return
 * the same number for every member while multiplying the load on the shared
 * n8n rate limit.
 */

/** Team fields needed to pick the member whose email fetches team credits. */
interface TeamWithMembers {
  owner_email?: string | null;
  /** list_teams shape. */
  members?: Array<{ email?: string | null; status?: string; user_id?: string | null }>;
  /** admin_client_overview shape. */
  memberships?: Array<{ email?: string | null; status?: string; user_id?: string | null }>;
}

/**
 * The identity used to fetch team-wide credits: an explicit owner_email when
 * present (admin_client_overview shape), otherwise the first ACCEPTED member
 * with a linked account (admin-prefs-manager list_teams shape), falling back
 * to any member with an email — the balance is identical for the whole team.
 */
export const resolveTeamOwnerEmail = (team: TeamWithMembers): string | null => {
  const explicit = String(team.owner_email || '').trim();
  if (explicit) return explicit;

  const roster = team.members || team.memberships || [];
  const accepted = roster.find((m) => m.status === 'accepted' && m.user_id && m.email);
  const anyWithEmail = roster.find((m) => m.email);
  const email = String((accepted || anyWithEmail)?.email || '').trim();
  return email || null;
};

/** Extract a numeric balance from a get_credit_balance response payload. */
export const parseCredits = (data: unknown): number | null => {
  const creditsData = Array.isArray(data) ? data[0] : data;
  const raw = creditsData?.balance ?? creditsData?.credits ?? null;
  const numeric = raw === null ? null : Number(raw);
  return numeric !== null && Number.isFinite(numeric) ? numeric : null;
};
